/**
 * Controller for the Activity Log (MongoDB-backed) view.
 * Fetches via the gateway WebSocket methods: activity-log.last, activity-log.query, activity-log.stats
 */

import type { GatewayBrowserClient } from "../gateway.ts";

export type ActivityLogLevel = "trace" | "debug" | "info" | "warn" | "error" | "fatal";
export type ActivityLogCategory =
  | "channel"
  | "agent"
  | "config"
  | "hook"
  | "session"
  | "system"
  | "command"
  | "webhook"
  | "queue"
  | "routing"
  | "plugin"
  | "auth"
  | "cron"
  | "model"
  | "media"
  | "node"
  | "tts"
  | "exec";

export type ActivityLogEntry = {
  seq: number;
  timestamp: string;
  category: ActivityLogCategory;
  action: string;
  level: ActivityLogLevel;
  source: string;
  message: string;
  details?: Record<string, unknown>;
  sessionKey?: string;
  sessionId?: string;
  channel?: string;
  agentId?: string;
  userId?: string;
  runId?: string;
  durationMs?: number;
  error?: string;
  instanceId?: string;
};

export type ActivityLogStats = {
  total: number;
  byCategory: Record<string, number>;
  byLevel: Record<string, number>;
  byChannel: Record<string, number>;
  oldest?: string;
  newest?: string;
};

export type ActivityLogState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  actLogLoading: boolean;
  actLogError: string | null;
  actLogEntries: ActivityLogEntry[];
  actLogStats: ActivityLogStats | null;
  actLogFilterText: string;
  actLogCategory: ActivityLogCategory | "all";
  actLogLevel: ActivityLogLevel | "all";
  actLogChannel: string;
  actLogAutoRefresh: boolean;
  actLogLimit: number;
  actLogSelectedEntry: ActivityLogEntry | null;
  actLogStatsLoading: boolean;
};

const ENTRY_LIMIT = 500;

export async function loadActivityLog(state: ActivityLogState, opts?: { reset?: boolean }) {
  if (!state.client || !state.connected) return;
  if (state.actLogLoading) return;
  state.actLogLoading = true;
  state.actLogError = null;
  try {
    const params: Record<string, unknown> = { limit: state.actLogLimit || 200 };
    if (state.actLogCategory !== "all") params.category = state.actLogCategory;
    if (state.actLogLevel !== "all") params.level = state.actLogLevel;
    if (state.actLogChannel) params.channel = state.actLogChannel;
    if (state.actLogFilterText.trim()) params.search = state.actLogFilterText.trim();
    params.sort = "desc";

    const res = (await state.client.request("activity-log.query", params)) as {
      count: number;
      entries: ActivityLogEntry[];
    };
    state.actLogEntries = (res.entries ?? []).slice(0, ENTRY_LIMIT);
  } catch (err) {
    state.actLogError = String(err);
  } finally {
    state.actLogLoading = false;
  }
}

export async function loadActivityLogLast(state: ActivityLogState, count = 100) {
  if (!state.client || !state.connected) return;
  if (state.actLogLoading) return;
  state.actLogLoading = true;
  state.actLogError = null;
  try {
    const res = (await state.client.request("activity-log.last", { count })) as {
      count: number;
      entries: ActivityLogEntry[];
    };
    state.actLogEntries = res.entries ?? [];
  } catch (err) {
    state.actLogError = String(err);
  } finally {
    state.actLogLoading = false;
  }
}

export async function loadActivityLogStats(state: ActivityLogState) {
  if (!state.client || !state.connected) return;
  state.actLogStatsLoading = true;
  try {
    const res = (await state.client.request("activity-log.stats")) as {
      stats: ActivityLogStats;
    };
    state.actLogStats = res.stats ?? null;
  } catch {
    // Stats are secondary — don't block UI.
  } finally {
    state.actLogStatsLoading = false;
  }
}
